import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { Users, UserPlus, FileText, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: stats?.total_clients || 0,
      icon: Users,
      color: '#ffd87a',
      trend: '+12%',
    },
    {
      title: 'Prospects',
      value: stats?.total_prospects || 0,
      icon: UserPlus,
      color: '#10b981',
      trend: '+8%',
    },
    {
      title: 'Portfolio Reviews',
      value: stats?.total_reviews || 0,
      icon: FileText,
      color: '#06b6d4',
      trend: '+5%',
    },
    {
      title: 'Recommendations',
      value: stats?.total_recommendations || 0,
      icon: TrendingUp,
      color: '#ffd87a',
      trend: '+15%',
    },
  ];

  const workflows = [
    {
      title: 'Stage 1: Input Selection',
      description: 'Select client/prospect and define report criteria',
      action: () => navigate('/workflow'),
    },
    {
      title: 'Stage 2: Risk Profiling',
      description: 'Assess risk profile and investment objectives',
      action: () => navigate('/risk-profiling'),
    },
    {
      title: 'Stage 3: Import Portfolio',
      description: 'Upload or import portfolio data',
      action: () => navigate('/portfolio'),
    },
    {
      title: 'Stage 4: Recommendations',
      description: 'View and manage central recommendations',
      action: () => navigate('/recommendations'),
    },
    {
      title: 'Stage 5: Portfolio Review',
      description: 'Analyze portfolio and identify deviations',
      action: () => navigate('/portfolio-review'),
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="dashboard-container">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            Welcome back, {user?.full_name}
          </h1>
          <p className="text-slate-400">Here's your portfolio management overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-xl" />
            ))
          ) : (
            statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="modern-card stat-card p-6 hover-lift fade-in"
                  data-testid={`stat-card-${index}`}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 rounded-lg bg-white/[0.05]" style={{borderLeft: `3px solid ${stat.color}`}}>
                      <Icon size={20} style={{color: stat.color}} />
                    </div>
                    <span className="text-xs font-medium text-green-400">{stat.trend}</span>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-2xl font-semibold text-white">{stat.value}</h3>
                    <p className="text-sm text-white/50">{stat.title}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Workflow Stages */}
        <div className="modern-card p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Workflow Stages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {workflows.map((workflow, index) => (
              <div
                key={index}
                className="group p-5 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-ugmonk-yellow/30 transition-all cursor-pointer"
                onClick={workflow.action}
                data-testid={`workflow-card-${index}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="step-indicator shrink-0">
                    {index + 1}
                  </div>
                  <ArrowRight size={16} className="text-white/20 mt-2 group-hover:text-ugmonk-grey/50 transition-colors" />
                </div>
                <h3 className="text-white font-medium text-sm mb-1.5">{workflow.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{workflow.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="modern-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/clients-prospects')}
              className="btn-primary"
              data-testid="quick-action-add-client"
            >
              <UserPlus size={16} className="mr-2" />
              Add Client
            </Button>
            <Button
              onClick={() => navigate('/workflow')}
              className="btn-primary"
              data-testid="quick-action-generate-report"
            >
              <FileText size={16} className="mr-2" />
              Generate Report
            </Button>
            <Button
              onClick={() => navigate('/portfolio')}
              className="btn-primary"
              data-testid="quick-action-upload-portfolio"
            >
              <TrendingUp size={16} className="mr-2" />
              Upload Portfolio
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;